import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api-client';
import { DashboardChecklist } from '@/components/onboarding/DashboardChecklist';
import { getOnboardingState, shouldShowOnboarding } from '@/lib/onboarding-state';

export function DashboardPage() {
  const [forms, setForms] = useState<unknown[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formsError, setFormsError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadForms();
  }, []);

  // Check if new user should be redirected to onboarding
  useEffect(() => {
    if (!isLoading && forms.length === 0) {
      const state = getOnboardingState();
      // If no onboarding state exists, this is a new user
      if (!state && shouldShowOnboarding()) {
        navigate('/app/setup', { replace: true });
      }
    }
  }, [isLoading, forms, navigate]);

  const loadForms = async () => {
    try {
      const data = await api.getForms();
      setForms(data.forms || []);
      setFormsError('');
    } catch (error) {
      console.error('Failed to load forms', error);
      setFormsError('Failed to load forms. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AppLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
        <p className="text-gray-600">Manage your forms and integrations</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">{forms.length}</CardTitle>
            <CardDescription>Active Forms</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">0</CardTitle>
            <CardDescription>Submissions Today</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">0</CardTitle>
            <CardDescription>Connected Chats</CardDescription>
          </CardHeader>
        </Card>
      </div>

      {/* Onboarding checklist - shows only if incomplete */}
      <DashboardChecklist />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Your Forms</CardTitle>
            <CardDescription>Manage and monitor your webhook forms</CardDescription>
          </div>
          <Link to="/app/forms">
            <Button>View All Forms</Button>
          </Link>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-gray-500">Loading forms...</p>
          ) : formsError ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{formsError}</p>
              <Button onClick={loadForms} variant="outline">
                Retry
              </Button>
            </div>
          ) : forms.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">You don't have any forms yet</p>
              <Link to="/app/forms">
                <Button>Create Your First Form</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {forms.slice(0, 5).map((form: any) => (
                <Link
                  key={form.id}
                  to={`/app/forms/${form.id}`}
                  className="block p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{form.name}</h3>
                      <p className="text-sm text-gray-500">{form.description || 'No description'}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-2 py-1 text-xs rounded ${form.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {form.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </AppLayout>
  );
}
